import { createSelector } from 'reselect'
import { handle } from 'redux-pack'
import { join } from 'remote/organizations'
import { getConfig } from 'config'
import { urls } from '../urls'
import {
  FETCH_ORGANIZATION,
  RESET_STATE,
  SUBMIT_USER_EMAIL,
  SUBMIT_USER_PASSWORD,
  SET_USER_INFORMATIONS,
  SET_ORGANIZATION_DETAILS,
  CHANGE_STEP,
  USER_PICKS_EMAIL_SIGNUP,
  SET_EMAIL_VERIFICATION_CODE,
  START_AUTO_JOIN_PROCESS,
  JOIN,
  SET_USER_OAUTH_INFORMATIONS_ON_REJOIN,
} from './actions'
import {
  fetchOrganizationPublicInfosByDomain,
  verifyOrganizationInviteToken,
} from 'remote/organizations'
import { JoinSteps } from './JoinSteps'

import { getReferrer, getUserFirstAttribution } from '../../../utils'

const INITIAL_STATE = {
  step: JoinSteps.Initialize,

  user: {
    email: '',
    displayName: '',
    password: '',
    photoURL: null,
    acceptedPrivacyPolicy: false,
    referrer: getReferrer(),
    firstAttribution: getUserFirstAttribution(),
    emailHandler: 'unknown',
  },

  joinableOrganizations: [],

  emailVerificationCode: {
    value: '',
    created: false,
    loading: false,
    error: null,
    invalid: false,
  },

  organization: {
    name: '',
    domain: '',
    photoURL: '',
    authDomains: '',
    membersCanAutoJoinByDomain: false,
  },

  fetchOrganization: {
    pending: false,
    error: null,
    result: null,
  },

  // This is an organization id
  // Set up when autoJoining an organization only
  autoJoining: null,
  isJoining: false,
  joiningError: false,
  joiningResult: null,

  authTokens: null,

  isCreating: false,
  creationError: false,
  creationResult: null,

  oauthError: false,

  joinOrigin: null,
}

// getOrganizationAppPath
export const getJoinState = (state) => state.organization.join
export const getStep = createSelector(getJoinState, ({ step }) => step)
export const getUser = createSelector(getJoinState, ({ user }) => user)
const getOrganizationInviteToken = createSelector(
  getJoinState,
  ({ organization }) => organization.organizationInviteToken
)

// In getJoinOrigin selector `oauthData.joinOrigin` has been added for
// when a user signup through SSO as we don't get the the joinOrigin parameter from the url, but it is still presents in the oauthData.
const getJoinOrigin = createSelector(
  getJoinState,
  ({ joinOrigin }) => joinOrigin
)

export const getEmailVerificationCode = createSelector(
  getJoinState,
  ({ emailVerificationCode }) => emailVerificationCode
)
export const getOrganization = createSelector(
  getJoinState,
  ({ organization }) => organization
)
const getAuthTokens = createSelector(
  getJoinState,
  ({ authTokens }) => authTokens
)

export const getAutoSignInPath = createSelector(
  getConfig,
  getOrganization,
  getAuthTokens,
  (config, { domain }, tokens) =>
    tokens && urls.getAutoSignUrl(config, domain, tokens)
)

// action creators
export function pickEmail() {
  return { type: USER_PICKS_EMAIL_SIGNUP }
}

export function init({ domain, organizationInviteToken, joinOrigin }) {
  return (dispatch, getState, config) => {
    dispatch({
      type: FETCH_ORGANIZATION,
      payload: {
        domain,
        organizationInviteToken,
      },
      promise: fetchOrganizationPublicInfosByDomain(config, {
        domain,
      })
        .then(async (organization) => {
          if (organizationInviteToken) {
            await verifyOrganizationInviteToken(config, {
              organizationId: organization.id,
              organizationInviteToken,
            })
          }
          return {
            ...organization,
            tokenIsValid: true,
            organizationInviteToken,
            joinOrigin,
          }
        })
        .catch(() => {
          throw new Error('We could not fetch the organization informations.')
        }),
    })
  }
}

export function submitUserEmail(data) {
  return {
    type: SUBMIT_USER_EMAIL,
    payload: data.email,
  }
}

export function submitUserPassword(password) {
  return {
    type: SUBMIT_USER_PASSWORD,
    payload: password,
  }
}

export function setEmailVerificationCode(emailVerificationCode) {
  return {
    type: SET_EMAIL_VERIFICATION_CODE,
    payload: emailVerificationCode,
  }
}

export function setUserInformations(userInformations) {
  return {
    type: SET_USER_INFORMATIONS,
    payload: {
      ...userInformations,
    },
  }
}

export function setOrganizationDetails(organization) {
  return (dispatch) => {
    dispatch({
      type: SET_ORGANIZATION_DETAILS,
      payload: { organization },
    })
  }
}

export function setStep(step) {
  return { type: CHANGE_STEP, payload: { step } }
}

export function joinOrganization(domain, userInformations) {
  return (dispatch, getState, config) => {
    const state = getState()
    const user = {
      ...getUser(state),
      ...userInformations,
    }
    const emailVerification = getEmailVerificationCode(state).value
    const organizationInviteToken = getOrganizationInviteToken(state)
    const joinOrigin = getJoinOrigin(state)

    dispatch({
      type: JOIN,
      payload: {
        domain,
        user,
      },
      promise: join(
        config,
        domain,
        user,
        emailVerification,
        organizationInviteToken,
        joinOrigin
      ),
    })
  }
}

export function backToStart() {
  return function goBack(dispatch, getState) {
    const user = getUser(getState())
    const isOauthSignup = user.oauthData !== null

    if (isOauthSignup) {
      return dispatch({
        type: RESET_STATE,
      })
    }

    return dispatch({
      type: CHANGE_STEP,
      payload: {
        step: JoinSteps.Start,
      },
    })
  }
}

export default function reducer(state = INITIAL_STATE, action) {
  const { type, payload } = action
  switch (type) {
    case FETCH_ORGANIZATION:
      return handle(state, action, {
        start: (s) => ({
          ...s,
          domain: payload.domain,
          organizationInviteToken: payload.organizationInviteToken,
          fetchOrganization: {
            pending: true,
            result: null,
            error: null,
          },
        }),
        failure: (s) => ({
          ...s,
          step: JoinSteps.Unjoinable,
          fetchOrganization: {
            ...s.fetchOrganization,
            error: payload,
          },
        }),
        success: (state) => {
          const {
            authMethod,
            membersCanAutoJoinByDomain,
            tokenIsValid = true,
            organizationInviteToken = null,
          } = payload
          if (payload === null) {
            return {
              step: JoinSteps.Inexistent,
            }
          }

          if (
            membersCanAutoJoinByDomain === false &&
            state.membershipStatus !== 'invitee' &&
            !organizationInviteToken
          ) {
            return {
              step: JoinSteps.Unjoinable,
            }
          }

          //todo step where invite token is wrong
          if (organizationInviteToken && !tokenIsValid) {
            return {
              step: JoinSteps.Unjoinable,
            }
          }

          const extra = {
            step: JoinSteps.Start,
          }

          // Login method is sso only, and the user hasn't logged in yet
          if (authMethod !== 'emailPassword' && !state.user.email) {
            extra.step = JoinSteps.FromSSO
          }

          // Happens when the user is trying to autojoin with an oauth provider
          // User comes from a creation step and clicked on a joinable organization
          if (state.user.email) {
            extra.step = JoinSteps.Password
            if (!!organizationInviteToken) {
              // Not autojoin, doesn't need to be in specific domain, and the api will return an error if so
              extra.step = JoinSteps.CredentialsSubmitted
            } else {
              if (state.user.oauthData) {
                extra.step = JoinSteps.CredentialsSubmitted
              }

              const isInAuthorizedDomain = (payload.authDomains || '')
                .split(',')
                .find(
                  (authDomain) => authDomain === state.user.email.split('@')[1]
                )

              if (!isInAuthorizedDomain) {
                extra.step = JoinSteps.Start
                extra.oauthError = true
              }
              if (state.membershipStatus === 'invitee') {
                extra.step = JoinSteps.EmailSent
              }
            }
          }
          return {
            ...state,
            ...extra,
            joinOrigin: payload.joinOrigin,
            fetchOrganization: {
              ...state.fetchOrganization,
              result: payload,
            },
            organization: payload,
          }
        },
        finish: (s) => ({
          ...s,
          fetchOrganization: {
            ...s.fetchOrganization,
            pending: false,
          },
        }),
      })
    case USER_PICKS_EMAIL_SIGNUP: {
      return { ...state, step: JoinSteps.Email }
    }

    case SUBMIT_USER_EMAIL: {
      return {
        ...state,
        user: {
          ...state.user,
          email: action.payload,
        },
      }
    }

    case SET_EMAIL_VERIFICATION_CODE: {
      return {
        ...state,
        emailVerificationCode: {
          ...state.emailVerificationCode,
          value: payload,
          error: null,
        },
      }
    }

    case SUBMIT_USER_PASSWORD: {
      return {
        ...state,
        user: {
          ...state.user,
          password: payload,
        },
        step: JoinSteps.CredentialsSubmitted,
      }
    }

    case SET_USER_INFORMATIONS: {
      const { displayName, photoURL } = payload

      return {
        ...state,
        step: JoinSteps.InformationsSubmitted,
        user: {
          ...state.user,
          displayName,
          photoURL,
        },
      }
    }

    //TODO: is this used?
    case SET_USER_OAUTH_INFORMATIONS_ON_REJOIN:
      return {
        ...state,
        user: {
          ...state.user,
          ...payload.user,
        },
      }

    case JOIN:
      return handle(state, action, {
        start: (s) => ({
          ...s,
          isJoining: true,
          step: JoinSteps.Joining,
          joiningResult: null,
          joiningError: false,
        }),
        failure: (s) => ({
          ...s,
          joiningError: payload,
        }),
        success: (s) => ({
          ...s,
          ...payload,
        }),
        finish: (s) => {
          return {
            ...s,
            isJoining: false,
            step: JoinSteps.Finished,
          }
        },
      })

    case START_AUTO_JOIN_PROCESS: {
      const isOauth = Boolean(state.user.cacheKey)

      return {
        ...state,
        ...payload,
        step: isOauth ? JoinSteps.CredentialsSubmitted : JoinSteps.Password,
      }
    }
    case SET_ORGANIZATION_DETAILS: {
      const { organization } = payload

      return {
        ...state,
        organization: {
          ...state.organization,
          ...organization,
        },
      }
    }

    case CHANGE_STEP: {
      const { step } = payload

      return { ...state, step }
    }

    case RESET_STATE: {
      return INITIAL_STATE
    }

    default:
      return state
  }
}
